import { MouseEvent, useEffect, useMemo, useRef } from 'react';

import type { CustomTestCategory, CustomTestCopyData, CustomTestDetail, CustomTestLanguage, CustomTestLifecycleState, CustomTestProduct, CustomTestWizardMode } from 'pages/custom_tests/types';
import type { SmartBuildClassroom, SmartBuildPurpose, SmartBuildScopeOption, SmartBuildScopeType, SmartBuildSizeChoice } from 'pages/custom_tests/components/custom_test_wizard/context';
import { EWizardMode, EWizardStep, getStepsForMode, isCreateMode } from 'pages/custom_tests/components/custom_test_wizard/machine';
import { TestSummaryBar } from 'pages/custom_tests/components/custom_test_wizard/components/test_summary_bar/TestSummaryBar';
import { WizardSuccess } from 'pages/custom_tests/components/custom_test_wizard/components/wizard_success/WizardSuccess';
import type { QuestionTree, QuestionTreeResult } from 'pages/custom_tests/components/question_picker/QuestionPicker';
import type { CustomTestWizardContextValue } from 'pages/custom_tests/components/custom_test_wizard/context';
import { AssignWizard, AssignWizardHandle } from 'pages/custom_tests/components/assign_wizard/AssignWizard';
import { TestSettingsStep } from 'pages/custom_tests/components/custom_test_wizard/steps/TestSettingsStep';
import { SmartBuildStep } from 'pages/custom_tests/components/custom_test_wizard/steps/SmartBuildStep';
import { QuestionsStep } from 'pages/custom_tests/components/custom_test_wizard/steps/QuestionsStep';
import { CustomTestWizardContext } from 'pages/custom_tests/components/custom_test_wizard/context';
import { SummaryStep } from 'pages/custom_tests/components/custom_test_wizard/steps/SummaryStep';
import { DetailsStep } from 'pages/custom_tests/components/custom_test_wizard/steps/DetailsStep';
import { GMetrixStepper, StepItem } from 'components/GMetrixStepper/GMetrixStepper';
import type { CustomTestProductQuestionCount } from 'apis/CustomTestApi';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { GMetrixLoader } from 'components/GMetrixLoader/GMetrixLoader';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { CUSTOM_TESTS_V2_BASE } from 'pages/custom_tests/types';
import { Dialog, DialogHandle } from 'components/modal_manager';
import type { BreadcrumbLink } from 'components/layout/page';
import { StudentGroupApi } from 'apis/StudentGroupApi';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNotification } from 'app/notifications';
import { PageHeader } from 'components/layout/page';
import { CustomTestApi } from 'apis/CustomTestApi';
import { LanguagesApi } from 'apis/LanguagesApi';
import { Alert, Card } from 'react-bootstrap';
import { useSuperState } from 'hooks';
import { useApp } from 'app';

type CustomTestWizardProps = {
    mode: CustomTestWizardMode;
};

// Wizard order-mode state → persisted QuestionOrderMode (1 = randomized, 2 = curriculum/grouped).
const orderModeToId = (orderMode: 'randomized' | 'grouped'): number => (orderMode === 'grouped' ? 2 : 1);

// Persisted QuestionOrderMode → wizard order-mode state (0/unspecified falls back to randomized).
const orderModeFromId = (questionOrderMode: number): 'randomized' | 'grouped' => (questionOrderMode === 2 ? 'grouped' : 'randomized');

export const CustomTestWizard = (props: CustomTestWizardProps) => {

    const { mode } = props;

    const [state, setState] = useSuperState({
        name: '',
        assignmentCount: 0,
        assignmentHistory: [] as string[],
        bankModeId: 2,
        categories: [] as CustomTestCategory[],
        categoryId: 0,
        editable: true,
        focusObjectiveId: 0,
        hasResults: false,
        isActive: true,
        isOwner: true,
        languageId: 0,
        languages: [] as CustomTestLanguage[],
        lifecycleState: 'draft' as CustomTestLifecycleState,
        loading: true,
        loadingProducts: false,
        loadingQuestions: false,
        minScore: 70,
        minScoreTouched: false,
        minutes: 60,
        minutesTouched: false,
        modeId: EWizardMode.TESTING as number,
        nameValidationError: '',
        orderMode: 'randomized' as 'randomized' | 'grouped',
        productIds: [] as number[],
        productQuestionCounts: [] as CustomTestProductQuestionCount[],
        products: [] as CustomTestProduct[],
        programDropdownKey: 0,
        questionIds: '',
        questionsSignature: '',
        questionsXml: '',
        questionTree: { Products: [] } as QuestionTree,
        randomly: 0,
        savedTestId: 0,
        saving: false,
        sbActivityState: '' as '' | 'none' | 'low' | 'sufficient',
        sbClassroomId: 0,
        sbClassrooms: [] as SmartBuildClassroom[],
        sbClassroomsError: '',
        sbClassroomsLoading: false,
        sbCustomCount: 0,
        sbGenerating: false,
        sbMyClassroomsOnly: true,
        sbPurpose: '' as SmartBuildPurpose | '',
        sbScopeType: '' as SmartBuildScopeType | '',
        sbScopeValueIds: [] as number[],
        sbSizeChoice: '' as SmartBuildSizeChoice | '',
        selectedQuestions: 0,
        settingsSeen: false,
        showSuccess: false,
        smartBuildRun: false,
        stepIndex: 0,
        testId: 0,
        useRandomly: false,
        validating: false,
        validationError: ''
    });

    const { name, bankModeId, categories, categoryId, editable, languageId, languages, lifecycleState, loading, loadingProducts,
        loadingQuestions, minScore, minutes, modeId, nameValidationError, orderMode, productIds, products, questionTree, randomly,
        savedTestId, saving, sbActivityState, sbClassroomId, sbClassrooms, sbClassroomsError, sbClassroomsLoading, sbCustomCount, sbGenerating, sbMyClassroomsOnly,
        sbPurpose, sbScopeType, sbScopeValueIds, sbSizeChoice, selectedQuestions, showSuccess, smartBuildRun, stepIndex,
        useRandomly, validating, validationError } = state;

    const { currentUser, selectedPartner } = useApp();
    const { formatMessage } = useIntl();
    const { createToast } = useNotification();
    const navigate = useNavigate();
    const { testId: routeTestId } = useParams();

    // Filtered lookup URL forwarded from the search page (possibly via the edit view), so
    // returning to the list restores the user's filters.
    const location = useLocation();
    const fromLookup = (location.state as { from?: string } | null)?.from;

    const assignWizardRef = useRef<AssignWizardHandle>(null);
    const dialogRef = useRef<DialogHandle>();

    // TEMP SB-FLAG: Smart Build feature flag bypassed for local/dev testing so the step
    // always shows regardless of the current user's feature ring. Restore before ship.
    // const smartBuildEnabled = isFeatureEnabled('Admin.CustomTest.SmartBuild');
    const smartBuildEnabled = true;

    const steps = getStepsForMode(mode, smartBuildEnabled);
    const currentStep = steps[stepIndex];
    const isLastStep = stepIndex === steps.length - 1;

    // From-scratch create flows only (copy prefills from the source test and keeps its values).
    const isNewTestFlow = mode === 'create-smart' || mode === 'create-manual';

    const backToList = () => navigate(fromLookup ?? CUSTOM_TESTS_V2_BASE);

    const fetchInitialData = async () => {

        try {

            const [categories, languages] = await Promise.all([
                CustomTestApi.GetCategories(selectedPartner.id),
                LanguagesApi.GetAll()
            ]) as [CustomTestCategory[], CustomTestLanguage[]];

            setState({
                categories: categories ?? [],
                languages: languages ?? [],

                // Create flows start with no question language selected (the Details step
                // forces an explicit choice); edit/copy keep the first-language seed as a
                // fallback for legacy tests persisted before LanguageId existed.
                ...(isNewTestFlow ? {} : { languageId: languages?.[0]?.Id ?? 0 })
            });

        } catch (e) {

            console.error(e);

        }

    };

    const fetchProducts = async (nextCategoryId: number) => {

        if (nextCategoryId <= 0) return;

        try {

            setState({ loadingProducts: true });
            const products = await CustomTestApi.GetProductsByCategory(nextCategoryId, selectedPartner.id);
            setState({ loadingProducts: false, products: products ?? [] });

        } catch (e) {

            console.error(e);
            setState({ loadingProducts: false });

        }

    };

    const fetchQuestions = async () => {

        if (!state.productIds.length) return;

        // Fall back to the first available language when no explicit question language is
        // set (legacy tests persisted before LanguageId existed can load with 0).
        const resolvedLanguageId = state.languageId || state.languages[0]?.Id || 0;

        // Cache guard: skip re-fetching (and losing/rebuilding the selection) when the
        // question-defining inputs have not changed since the last successful load.
        const signature = `${state.productIds.join(',')}|${state.categoryId}|${resolvedLanguageId}|${state.bankModeId}`;
        if (signature === state.questionsSignature && (state.questionTree.Products?.length ?? 0) > 0) return;

        try {

            setState({ loadingQuestions: true });

            const result: QuestionTreeResult = await CustomTestApi.GetQuestions(
                state.productIds.join(','),
                state.categoryId,
                resolvedLanguageId,
                state.bankModeId,
                state.questionIds
            );

            setState({
                loadingQuestions: false,
                questionsSignature: signature,
                questionsXml: result.XmlString,
                questionTree: result
            });

        } catch (e: any) {

            setState({ loadingQuestions: false });
            createToast(e?.message ?? (formatMessage({ id: 'failed_to_load_questions.82bf5a85', defaultMessage: 'Failed to load questions.' })), 'danger');

        }

    };

    const loadExistingTest = async (id: number, forCopy: boolean) => {

        try {

            setState({ loading: true });

            if (forCopy) {

                const data: CustomTestCopyData = await CustomTestApi.GetForCopy(id);

                // Copy prefills every field from the source test; the only intended
                // difference is the name, which gets a unique "(Copy)"/"(Copy N)" suffix.
                const sourceName = (data.Name ?? '').trim();
                const rootName = sourceName.replace(/\s*\(Copy(?:\s+\d+)?\)\s*$/i, '');
                const baseCopyName = `${rootName} (Copy)`;

                let copyName = baseCopyName;

                try {

                    if (!await CustomTestApi.ValidateTestName(baseCopyName, selectedPartner.id)) {

                        for (let suffix = 2; suffix <= 20; suffix++) {

                            const candidate = `${rootName} (Copy ${suffix})`;

                            if (await CustomTestApi.ValidateTestName(candidate, selectedPartner.id)) {

                                copyName = candidate;
                                break;

                            }

                        }

                    }

                } catch {

                    // Fall back to the base name silently; the create-time name
                    // validation will surface any remaining collision.

                }

                setState({
                    name: copyName,

                    // Hybrid categories carry two question banks; the source test's
                    // HasApplicationQuestions flag identifies which bank it was authored from
                    // (true = in-app, false = browser).
                    bankModeId: data.HasApplicationQuestions ? 1 : 2,
                    categoryId: data.CategoryId ?? 0,
                    editable: true,
                    lifecycleState: 'draft',
                    loading: false,
                    minScore: data.MinScore ?? 70,
                    minutes: data.Minutes ?? 60,
                    modeId: data.ModeId ?? EWizardMode.TESTING,
                    orderMode: orderModeFromId(data.QuestionOrderMode ?? 0),
                    productIds: data.ProductIds ?? [],
                    questionIds: data.SelectedQuestionIds ?? '',
                    questionsXml: data.QuestionsXml ?? '',
                    randomly: data.Randomly ?? 0,
                    selectedQuestions: (data.SelectedQuestionIds ?? '').split(',').filter(Boolean).length,
                    useRandomly: data.UseRandomly ?? false,

                    // Only override the question language when the source persisted one;
                    // otherwise keep the default seeded from the languages list.
                    ...(data.LanguageId ? { languageId: data.LanguageId } : {})
                });

                await fetchProducts(data.CategoryId ?? 0);
                return;

            }

            const test: CustomTestDetail = await CustomTestApi.Get(id);

            setState({
                name: test.Name ?? '',
                assignmentCount: test.AssignmentCount ?? 0,

                // Hybrid categories carry two question banks; the test's
                // HasApplicationQuestions flag identifies which bank it was authored from
                // (true = in-app, false = browser).
                bankModeId: test.HasApplicationQuestions ? 1 : 2,
                categoryId: test.CategoryId ?? 0,
                editable: test.Editable,
                hasResults: test.HasResults ?? false,
                isActive: test.IsActive ?? true,
                isOwner: test.IsOwner ?? true,
                lifecycleState: test.LifecycleState,
                loading: false,
                minScore: test.MinScore ?? 70,
                minutes: test.Minutes ?? 60,
                modeId: test.ModeId ?? EWizardMode.TESTING,
                orderMode: orderModeFromId(test.QuestionOrderMode ?? 0),
                productIds: test.ProductIds ?? [],
                questionIds: test.SelectedQuestionIds ?? '',
                questionsXml: test.QuestionsXml ?? '',
                randomly: test.Randomly ?? 0,
                selectedQuestions: test.NumQuestions ?? 0,
                testId: id,
                useRandomly: test.UseRandomly ?? false,

                // Only override the question language when the test persisted one;
                // otherwise keep the default seeded from the languages list.
                ...(test.LanguageId ? { languageId: test.LanguageId } : {})
            });

            await fetchProducts(test.CategoryId ?? 0);

        } catch (e: any) {

            setState({ loading: false });
            createToast(e?.message ?? (formatMessage({ id: 'failed_to_load_test_data.6fe1536e', defaultMessage: 'Failed to load test data.' })), 'danger');

        }

    };

    // Question state invalidated by upstream (category/program/language/bank) changes,
    // including any Smart Build values so a stale draft/scope cannot survive the change
    // (scope value ids reference the old tree's topics/objectives).
    const clearedQuestionState = {
        questionIds: '',
        questionsSignature: '',
        questionsXml: '',
        questionTree: { Products: [] } as QuestionTree,
        sbActivityState: '' as const,
        sbCustomCount: 0,
        sbScopeType: '' as SmartBuildScopeType | '',
        sbScopeValueIds: [] as number[],
        sbSizeChoice: '' as SmartBuildSizeChoice | '',
        selectedQuestions: 0,
        smartBuildRun: false
    };

    // Changing the question pool after questions were selected silently discards that
    // selection, so confirm first. With nothing selected the change applies immediately.
    const confirmQuestionInvalidation = (applyChange: () => void) => {

        const dialog = dialogRef.current;

        if (state.selectedQuestions <= 0 || !dialog) {

            applyChange();
            return;

        }

        dialog.show({
            actions: [
                dialog.Action({
                    label: formatMessage({ id: 'cancel.89a86adc', defaultMessage: 'Cancel' }),

                    // Remount the Program dropdown so its internal pending selection
                    // re-initializes from the (unchanged) controlled value instead of
                    // showing the rejected pick until reopened.
                    onClick: () => setState(prev => ({ programDropdownKey: prev.programDropdownKey + 1 })),
                    variant: 'outline-secondary'
                }),
                dialog.Action({
                    label: formatMessage({ id: 'continue.1569eae3', defaultMessage: 'Continue' }),
                    onClick: () => {

                        setState(clearedQuestionState);
                        applyChange();

                    },
                    variant: 'danger'
                })
            ],
            body: formatMessage({ id: 'changing_this_will_clear_your_selected_q.868f8475', defaultMessage: 'Changing this will clear your selected questions.' }),
            centered: true,
            size: 'md',
            title: formatMessage({ id: 'clear_selected_questions.f1539eba', defaultMessage: 'Clear selected questions?' })
        });

    };

    const handleToggleCategory = (nextCategoryId: number) => {

        if (nextCategoryId === state.categoryId) return;

        confirmQuestionInvalidation(() => {

            setState({
                ...clearedQuestionState,
                categoryId: nextCategoryId,
                productIds: [],
                products: []
            });

            fetchProducts(nextCategoryId);

        });

    };

    const handleProductsChange = (nextProductIds: number[]) => {

        const unchanged = nextProductIds.length === state.productIds.length
            && nextProductIds.every(id => state.productIds.includes(id));
        if (unchanged) return;

        confirmQuestionInvalidation(() => setState({ productIds: nextProductIds }));

    };

    const handleToggleProduct = (productId: number) => {

        const updated = state.productIds.includes(productId)
            ? state.productIds.filter(id => id !== productId)
            : [...state.productIds, productId];

        handleProductsChange(updated);

    };

    const handleLanguageChange = (nextLanguageId: number) => {

        if (nextLanguageId === state.languageId) return;
        confirmQuestionInvalidation(() => setState({ languageId: nextLanguageId }));

    };

    const handleBankModeChange = (nextBankModeId: number) => {

        if (nextBankModeId === state.bankModeId) return;
        confirmQuestionInvalidation(() => setState({ bankModeId: nextBankModeId }));

    };

    const handleQuestionsChange = (questionIds: string, totalSelected: number) => {

        // On the from-scratch create flows, seed the time limit at ~1 minute per question
        // until the user manually adjusts it. Copy keeps the source test's time limit.
        const syncMinutes = isNewTestFlow && !state.minutesTouched && totalSelected > 0;

        setState({
            questionIds,
            selectedQuestions: totalSelected,
            ...(syncMinutes ? { minutes: totalSelected } : {})
        });

    };

    // ============================================================================
    // Smart Build — statement-builder step (For [Classroom], build a test to
    // [Purpose] using content from [Scope] with [Size] questions).
    //
    // The ranked draft is generated SERVER-SIDE by the recommendation engine
    // (SmartBuildRecommend, WI 40701): eligible-pool build, objective ranking by
    // Purpose (§9.4), within-objective ordering by Average Recency (§13.1/§13.3),
    // and engagement-viability evaluation (§12). The client-side question tree is
    // used only for the Scope options, eligible counts, and a pre-flight guard —
    // the engine's pool mirrors it, so every recommended id exists in the tree the
    // Questions step renders next.
    // ============================================================================

    const fetchSbClassrooms = async () => {

        if (state.sbClassrooms.length || state.sbClassroomsLoading) return;

        try {

            setState({ sbClassroomsError: '', sbClassroomsLoading: true });

            const groups = await StudentGroupApi.GetAll('Partner');

            const classrooms: SmartBuildClassroom[] = (groups ?? [])
                .filter(g => !(g as any).Archived)
                .map(g => ({
                    Id: g.Id,
                    Instructor: typeof (g as any).Instructors === 'string' ? (g as any).Instructors : '',
                    Name: g.Name ?? '',
                    OwnerUserId: (g as any).UserId ?? 0
                }));

            setState({ sbClassrooms: classrooms, sbClassroomsLoading: false });

        } catch (e: any) {

            setState({
                sbClassroomsError: e?.message ?? (formatMessage({ id: 'failed_to_load_classrooms.afc12d1d', defaultMessage: 'Failed to load classrooms.' })),
                sbClassroomsLoading: false
            });

        }

    };

    // Flat eligible pool for the current Smart Build scope, in curriculum order.
    const collectScopedQuestionIds = (): number[] => {

        const ids: number[] = [];
        const seen = new Set<number>();
        const valueIds = new Set(state.sbScopeValueIds);

        for (const product of state.questionTree.Products ?? []) {

            for (const theme of product.Themes ?? []) {

                if (state.sbScopeType === 'topic' && !valueIds.has(theme.ThemeId)) continue;

                for (const objective of theme.Objectives ?? []) {

                    if (state.sbScopeType === 'objective' && !valueIds.has(objective.ObjectiveId)) continue;

                    for (const question of objective.Questions ?? []) {

                        if (seen.has(question.QuestionId)) continue;
                        seen.add(question.QuestionId);
                        ids.push(question.QuestionId);

                    }

                }

            }

        }

        return ids;

    };

    const executeSmartBuild = async () => {

        // Client-side eligible pool is only used as a guard + for logging; the actual
        // ranked selection is computed server-side from classroom aggregate metrics.
        const eligible = collectScopedQuestionIds();
        if (!eligible.length) return;

        const requested = state.sbSizeChoice === 'quick'
            ? 10
            : state.sbSizeChoice === 'standard' ? 25 : state.sbCustomCount;
        const size = Math.min(Math.max(1, requested || 1), eligible.length);

        // Scope Type (§10.3): '' / 'all' = All Available Content (0), Topic = Theme (1),
        // Objective (2). Kept in lockstep with the proc's @ScopeType contract.
        const scopeType = state.sbScopeType === 'topic' ? 1 : state.sbScopeType === 'objective' ? 2 : 0;

        // Same language the Questions step fetches with, so recommended ids exist in the
        // tree the wizard shows next (legacy tests can load with 0).
        const resolvedLanguageId = state.languageId || state.languages[0]?.Id || 0;

        const request = {
            categoryId: state.categoryId,
            classroomId: state.sbClassroomId,
            deliveryType: state.bankModeId,
            languageId: resolvedLanguageId,
            productIds: state.productIds,
            purpose: state.sbPurpose || '',
            scopeType,
            scopeValueIds: scopeType === 0 ? [] : state.sbScopeValueIds,
            size
        };

        // TEMP SB-LOG: one compact line per request so the full feature can be driven
        // from the form and validated against the Smart Build document. Remove before ship.
        // eslint-disable-next-line no-console
        if (import.meta.env.DEV) console.log('[SB] request', JSON.stringify(request));

        try {

            setState({ sbGenerating: true, validationError: '' });

            const recommendation = await CustomTestApi.SmartBuildRecommend(request);

            // TEMP SB-LOG: full engine response (ranking outcome, viability, quality).
            // eslint-disable-next-line no-console
            if (import.meta.env.DEV) console.log('[SB] response', JSON.stringify(recommendation));

            const picked = (recommendation.SelectedQuestionIds || '')
                .split(',')
                .map(id => id.trim())
                .filter(id => id.length > 0);

            if (!picked.length) {

                setState({ sbGenerating: false, validationError: recommendation.Message });
                return;

            }

            // Route through the shared handler so time-limit seeding behaves exactly like
            // a manual selection would.
            handleQuestionsChange(picked.join(','), picked.length);

            // Engagement viability (§12): keep the evaluated state so the activity notice
            // reflects the engine's verdict, and disclose low/no-activity drafts up front.
            const activityState = (recommendation.Viability?.State ?? '') as '' | 'none' | 'low' | 'sufficient';

            if (activityState === 'none' || activityState === 'low') {

                createToast(
                    formatMessage({ id: 'this_classroom_has_limited_activity_for.599d731c', defaultMessage: 'This classroom has limited activity for the selected scope, so the draft is based more on content structure than classroom performance.' }),
                    'warning',
                    8000
                );

            }

            // Purpose pre-populates Test Settings (§13.4): the engine returns the suggested
            // Mode (1 = Training, 2 = Testing) and Question Order; the user can still change
            // both later on the Test Settings step.
            setState({
                modeId: recommendation.SuggestedModeId,
                orderMode: recommendation.SuggestedOrderMode === 'grouped' ? 'grouped' : 'randomized',
                sbActivityState: activityState,
                sbGenerating: false,
                smartBuildRun: true,
                validationError: ''
            });

            // Generation lands the draft on the Questions step, pre-selected and editable.
            goToStep(stepIndex + 1);

        } catch (e) {

            // TEMP SB-LOG: surface engine failures while driving the feature.
            // eslint-disable-next-line no-console
            if (import.meta.env.DEV) console.log('[SB] error', e);
            setState({ sbGenerating: false, validationError: (e as Error)?.message ?? 'Smart Build failed.' });

        }

    };

    const runSmartBuild = () => {

        // Regenerating over an existing draft (or manual selections) is destructive,
        // so confirm before replacing them (Replace Draft dialog).
        const dialog = dialogRef.current;

        if ((state.smartBuildRun || state.selectedQuestions > 0) && dialog) {

            dialog.show({
                actions: [
                    dialog.Action({
                        label: formatMessage({ id: 'cancel.89a86adc', defaultMessage: 'Cancel' }),
                        variant: 'outline-secondary'
                    }),
                    dialog.Action({
                        label: formatMessage({ id: 'replace_draft.fd384c51', defaultMessage: 'Replace Draft' }),
                        onClick: executeSmartBuild,
                        variant: 'warning'
                    })
                ],
                body: (
                    <div className={'text-center py-2'}>
                        <div
                            className={'d-flex align-items-center justify-content-center rounded-circle bg-warning text-white mx-auto mb-3'}
                            style={{ height: 48, width: 48 }}
                        >
                            <i className={'fa-solid fa-exclamation'} />
                        </div>
                        <h5 className={'fw-bold mb-2'}>
                            <FormattedMessage defaultMessage="Replace your current draft?" id="replace_your_current_draft.a3b5767f" />
                        </h5>
                        <p className={'text-muted mb-0'}>
                            <FormattedMessage defaultMessage="Replacing the draft will remove your current question selections, including any manual changes, and update any settings populated by Smart Build." id="replacing_the_draft_will_remove_your_cur.ecd7f208" />
                        </p>
                    </div>
                ),
                centered: true,
                size: 'md',
                title: formatMessage({ id: 'replace_draft.fd384c51', defaultMessage: 'Replace Draft' })
            });

            return;

        }

        void executeSmartBuild();

    };

    const skipSmartBuild = () => {

        // Skipping past an existing draft or manual selection must never destroy it:
        // move ahead with everything intact. Only with nothing selected does skip
        // clear the (empty) draft state, keeping the loaded tree (the Questions step
        // seeds strictly from questionIds, so a cached tree cannot re-select anything
        // on its own).
        if (!state.smartBuildRun && state.selectedQuestions <= 0) {

            setState({
                questionIds: '',
                selectedQuestions: 0,
                smartBuildRun: false
            });

        }

        goToStep(stepIndex + 1);

    };

    // Scope setters own the downstream clearing rules: changing the Scope Type
    // resets its values and the Size choice; changing values revalidates Size.
    // Either change invalidates the last engine viability evaluation.
    const setSbScopeType = (scopeType: SmartBuildScopeType | '') => {

        setState({
            sbActivityState: '',
            sbCustomCount: 0,
            sbScopeType: scopeType,
            sbScopeValueIds: [],
            sbSizeChoice: ''
        });

    };

    const setSbScopeValueIds = (ids: number[]) => {

        setState({
            sbActivityState: '',
            sbCustomCount: 0,
            sbScopeValueIds: ids,
            sbSizeChoice: ''
        });

    };

    const setSbSizeChoice = (choice: SmartBuildSizeChoice | '') => {

        setState({ sbSizeChoice: choice });

    };

    const validateDetails = async () => {

        if (!state.name.trim()) {

            setState({ nameValidationError: formatMessage({ id: 'test_name_is_required.88a8be5d', defaultMessage: 'Test name is required.' }), validationError: '' });
            return false;

        }

        if (state.languageId <= 0) {

            setState({ nameValidationError: '', validationError: formatMessage({ id: 'please_select_a_question_language.77fdeb90', defaultMessage: 'Please select a question language.' }) });
            return false;

        }

        if (state.productIds.length === 0) {

            setState({ nameValidationError: '', validationError: formatMessage({ id: 'please_select_at_least_one_product.2158e646', defaultMessage: 'Please select at least one product.' }) });
            return false;

        }

        // Uniqueness is only enforced when the name can collide with an existing record
        // (create/copy). Edit keeps its existing name.
        if (isCreateMode(mode)) {

            try {

                setState({ nameValidationError: '', validating: true, validationError: '' });
                const isUnique = await CustomTestApi.ValidateTestName(state.name.trim(), selectedPartner.id);
                setState({ validating: false });

                if (!isUnique) {

                    setState({ nameValidationError: formatMessage({ id: 'test_name_already_in_use.d76e3541', defaultMessage: 'Test Name already in use.' }) });
                    return false;

                }

            } catch {

                setState({ validating: false, validationError: formatMessage({ id: 'unable_to_validate_test_name_please_try.de4a12c1', defaultMessage: 'Unable to validate test name. Please try again.' }) });
                return false;

            }

        }

        setState({ nameValidationError: '', validationError: '' });
        return true;

    };

    const validateQuestions = () => {

        if (state.selectedQuestions === 0) {

            setState({ validationError: formatMessage({ id: 'please_select_at_least_one_question.c98fa9f5', defaultMessage: 'Please select at least one question.' }) });
            return false;

        }

        if (state.useRandomly && (state.randomly < 1 || state.randomly > state.selectedQuestions)) {

            setState({ validationError: formatMessage({ id: 'random_question_count_must_be_between_1.b044613b', defaultMessage: 'Random question count must be between 1 and the total selected questions.' }) });
            return false;

        }

        setState({ validationError: '' });
        return true;

    };

    // First invalid test setting, or '' when everything is in range. Shared between the
    // Settings step validation and the pre-save gate.
    const getSettingsError = (): string => {

        if (state.modeId <= 0) return formatMessage({ id: 'please_select_a_testing_mode.717f3558', defaultMessage: 'Please select a testing mode.' });
        if (state.minScore < 1 || state.minScore > 100) return formatMessage({ id: 'passing_score_must_be_between_1_and_100.42ca0555', defaultMessage: 'Passing score must be between 1 and 100.' });
        if (state.minutes < 1 || state.minutes > 600) return formatMessage({ id: 'time_limit_must_be_between_1_and_600_min.e27649b8', defaultMessage: 'Time limit must be between 1 and 600 minutes.' });
        if (state.useRandomly && (state.randomly < 1 || state.randomly > state.selectedQuestions)) return formatMessage({ id: 'random_question_count_must_be_between_1.b044613b', defaultMessage: 'Random question count must be between 1 and the total selected questions.' });
        return '';

    };

    const validateSettings = () => {

        const error = getSettingsError();
        setState({ validationError: error });
        return !error;

    };

    const validateStep = async () => {

        switch (currentStep) {

            case EWizardStep.DETAILS:
                return await validateDetails();
            case EWizardStep.QUESTIONS:
                return validateQuestions();
            case EWizardStep.SETTINGS:
                return validateSettings();
            default:
                setState({ validationError: '' });
                return true;

        }

    };

    const goToStep = (index: number) => {

        if (index < 0 || index > steps.length - 1) return;
        const reachedSettings = steps[index] === EWizardStep.SETTINGS || steps[index] === EWizardStep.SUMMARY;
        setState({ settingsSeen: state.settingsSeen || reachedSettings, stepIndex: index, validationError: '' });

    };

    const editStep = (step: EWizardStep) => {

        const index = steps.indexOf(step);
        if (index >= 0) goToStep(index);

    };

    const handleNext = async () => {

        if (!await validateStep()) return;
        goToStep(stepIndex + 1);

    };

    const handleBack = () => goToStep(stepIndex - 1);

    const handleStepperChange = async (index: number) => {

        if (index === stepIndex) return;

        // Moving forward requires the current step to validate.
        if (index > stepIndex && !await validateStep()) return;
        goToStep(index);

    };

    // First reason the test cannot be saved yet, or '' when it is ready to post.
    const getSaveBlocker = (): string => {

        if (!state.name.trim()) return formatMessage({ id: 'test_name_is_required.88a8be5d', defaultMessage: 'Test name is required.' });
        if (!state.productIds.length) return formatMessage({ id: 'please_select_at_least_one_product.2158e646', defaultMessage: 'Please select at least one product.' });
        if (state.selectedQuestions <= 0) return formatMessage({ id: 'please_select_at_least_one_question.c98fa9f5', defaultMessage: 'Please select at least one question.' });
        return getSettingsError();

    };

    const handleSave = async () => {

        const blocker = getSaveBlocker();

        if (blocker) {

            createToast(blocker, 'danger');
            return;

        }

        try {

            setState({ saving: true });

            const response = isCreateMode(mode)
                ? await CustomTestApi.Create({
                    categoryId: state.categoryId,
                    languageId: state.languageId,
                    minScore: state.minScore,
                    minutes: state.minutes,
                    modeId: state.modeId,
                    multiProductTest: state.productIds.length > 1,
                    productId: state.productIds[0] ?? 0,
                    questionIds: state.questionIds,
                    questionOrderMode: orderModeToId(state.orderMode),
                    questionsXml: state.questionsXml,
                    randomly: state.useRandomly ? state.randomly : 0,
                    testName: state.name,
                    totalQuestions: state.selectedQuestions,
                    useRandomly: state.useRandomly
                })
                : await CustomTestApi.Update({
                    isActive: state.isActive,
                    languageId: state.languageId,
                    minScore: state.minScore,
                    minutes: state.minutes,
                    modeId: state.modeId,
                    multiProductTest: state.productIds.length > 1,
                    productId: state.productIds.join(','),
                    questionIds: state.questionIds,
                    questionOrderMode: orderModeToId(state.orderMode),
                    questionsXml: state.questionsXml,
                    randomly: state.useRandomly ? state.randomly : 0,
                    testId: state.testId,
                    testName: state.name,
                    totalQuestions: state.selectedQuestions,
                    useRandomly: state.useRandomly
                });

            if (response?.Done) {

                createToast(
                    mode === 'edit'
                        ? (formatMessage({ id: 'test_updated_successfully.1d9dd27d', defaultMessage: 'Test updated successfully.' }))
                        : (formatMessage({ id: 'custom_test_created_successfully.9197a4d4', defaultMessage: 'Custom test created successfully.' })),
                    'success',
                    5000
                );

                // Edit mode returns to the saved test's Details view rather than showing the
                // create-style post-save success screen.
                if (mode === 'edit') {

                    setState({ saving: false });
                    navigate(`${CUSTOM_TESTS_V2_BASE}/edit/${state.testId}`, { state: { from: fromLookup } });
                    return;

                }

                // Create resolves with the new test id (as a string) in the response
                // message, so the success actions can deep-link to it directly.
                const savedId = isCreateMode(mode)
                    ? parseInt(response?.Message ?? '', 10) || 0
                    : state.testId;

                setState({ savedTestId: savedId, saving: false, showSuccess: true });

            } else {

                createToast(response?.Message ?? (formatMessage({ id: 'failed_to_save_custom_test.9253bafe', defaultMessage: 'Failed to save custom test.' })), 'danger');
                setState({ saving: false });

            }

        } catch (e: any) {

            createToast(e?.message ?? (formatMessage({ id: 'an_error_occurred_while_saving_the_test.c929208a', defaultMessage: 'An error occurred while saving the test.' })), 'danger');
            setState({ saving: false });

        }

    };

    useEffect(() => {

        const bootstrap = async () => {

            await fetchInitialData();

            const id = parseInt(routeTestId ?? '0', 10);

            if ((mode === 'edit' || mode === 'copy') && id > 0) {

                await loadExistingTest(id, mode === 'copy');

            } else {

                // Create flows start with no testing mode selected (the Settings step
                // forces an explicit choice); edit/copy keep the loaded test's mode.
                setState({ loading: false, ...(isNewTestFlow ? { modeId: 0 } : {}) });

            }

        };

        bootstrap();

    }, []);

    // Create flows: eligible-question counts per program for the selected category. They
    // filter the Program dropdown (programs with no questions are hidden) and provide the
    // default passing score seed below.
    useEffect(() => {

        if (!isNewTestFlow || categoryId <= 0) return;

        const resolvedLanguageId = state.languageId || state.languages[0]?.Id || 0;

        CustomTestApi.GetProductQuestionCounts(categoryId, resolvedLanguageId, bankModeId)
            .then(counts => setState({ productQuestionCounts: counts ?? [] }))
            .catch(e => console.error(e));

    }, [categoryId, languageId, bankModeId]);

    // Seed the passing score from the first selected program's configured default until
    // the user adjusts the score themselves.
    useEffect(() => {

        if (!isNewTestFlow || state.minScoreTouched || !productIds.length) return;

        const firstProduct = state.productQuestionCounts.find(count => count.ProductId === productIds[0]);
        if (firstProduct?.DefaultPassingScore != null) setState({ minScore: firstProduct.DefaultPassingScore });

    }, [productIds, state.productQuestionCounts]);

    // Entering Smart Build loads its inputs: the question tree (scope options, eligible
    // counts, and the generation pool all come from it) and the classroom list.
    useEffect(() => {

        if (currentStep !== EWizardStep.SMART_BUILD || productIds.length !== 1) return;

        fetchQuestions();
        fetchSbClassrooms();

    }, [currentStep]); // eslint-disable-line react-hooks/exhaustive-deps

    // Topics (themes) and objectives available for Smart Build Scope, with eligible
    // question counts, in curriculum order.
    const sbTopics: SmartBuildScopeOption[] = useMemo(() => {

        const topics: SmartBuildScopeOption[] = [];
        for (const product of questionTree.Products ?? []) {

            for (const theme of product.Themes ?? []) {

                const count = (theme.Objectives ?? []).reduce((sum, objective) => sum + (objective.Questions?.length ?? 0), 0);
                if (count > 0) topics.push({ Id: theme.ThemeId, Name: theme.Name, QuestionCount: count });

            }

        }
        return topics;

    }, [questionTree]);

    const sbObjectives: SmartBuildScopeOption[] = useMemo(() => {

        const objectives: SmartBuildScopeOption[] = [];
        for (const product of questionTree.Products ?? []) {

            for (const theme of product.Themes ?? []) {

                for (const objective of theme.Objectives ?? []) {

                    const count = objective.Questions?.length ?? 0;
                    if (count > 0) objectives.push({ Id: objective.ObjectiveId, Name: objective.Name, QuestionCount: count });

                }

            }

        }
        return objectives;

    }, [questionTree]);

    const sbScopeComplete = sbScopeType === 'all' || (!!sbScopeType && sbScopeValueIds.length > 0);

    const sbEligibleCount = useMemo(() => {

        if (!sbScopeComplete) return 0;

        if (sbScopeType === 'topic') {

            return sbTopics.filter(topic => sbScopeValueIds.includes(topic.Id)).reduce((sum, topic) => sum + topic.QuestionCount, 0);

        }

        if (sbScopeType === 'objective') {

            return sbObjectives.filter(objective => sbScopeValueIds.includes(objective.Id)).reduce((sum, objective) => sum + objective.QuestionCount, 0);

        }

        return sbTopics.reduce((sum, topic) => sum + topic.QuestionCount, 0);

    }, [sbScopeComplete, sbScopeType, sbScopeValueIds, sbTopics, sbObjectives]);

    // Size preset adaptation (spec 11.3/11.4): with 10 or fewer eligible questions the
    // presets disappear and the count input shows directly, defaulted to the total.
    useEffect(() => {

        if (!sbScopeComplete || sbEligibleCount <= 0) return;

        if (sbEligibleCount <= 10 && sbSizeChoice !== 'custom') {

            setState({ sbCustomCount: sbEligibleCount, sbSizeChoice: 'custom' });
            return;

        }

        if (sbSizeChoice === 'custom' && state.sbCustomCount <= 0) {

            setState({ sbCustomCount: sbEligibleCount });

        }

    }, [sbScopeComplete, sbEligibleCount, sbSizeChoice]); // eslint-disable-line react-hooks/exhaustive-deps

    // Auto-select the only available Scope Value (spec 10.4).
    useEffect(() => {

        if (sbScopeType === 'topic' && sbTopics.length === 1 && !sbScopeValueIds.length) setState({ sbScopeValueIds: [sbTopics[0].Id] });
        if (sbScopeType === 'objective' && sbObjectives.length === 1 && !sbScopeValueIds.length) setState({ sbScopeValueIds: [sbObjectives[0].Id] });

    }, [sbScopeType, sbTopics, sbObjectives]); // eslint-disable-line react-hooks/exhaustive-deps

    // Generate is available only once the whole statement is complete against a
    // single program with at least one eligible question in scope.
    const sbSizeComplete = sbSizeChoice === 'quick' || sbSizeChoice === 'standard' || (sbSizeChoice === 'custom' && sbCustomCount >= 1);
    const sbCanGenerate = productIds.length === 1 && sbClassroomId > 0 && !!sbPurpose
        && sbScopeComplete && sbSizeComplete && sbEligibleCount > 0 && !loadingQuestions && !sbGenerating;

    const stepLabels: Record<EWizardStep, string> = {
        [EWizardStep.DETAILS]: formatMessage({ id: 'test_details.88f73a4e', defaultMessage: 'Test Details' }),
        [EWizardStep.QUESTIONS]: formatMessage({ id: 'questions.940c8cce', defaultMessage: 'Questions' }),
        [EWizardStep.SETTINGS]: formatMessage({ id: 'test_settings.dfedf589', defaultMessage: 'Test Settings' }),
        [EWizardStep.SMART_BUILD]: formatMessage({ id: 'smart_build.7cf76046', defaultMessage: 'Smart Build' }),
        [EWizardStep.SUMMARY]: formatMessage({ id: 'summary.b52cf991', defaultMessage: 'Summary' })
    };

    const stepItems: StepItem[] = steps.map(step => ({ label: stepLabels[step] }));

    const titleByMode: Record<CustomTestWizardMode, string> = {
        copy: formatMessage({ id: 'edit_custom_test.878c4842', defaultMessage: 'Edit Custom Test' }),
        'create-manual': formatMessage({ id: 'create_custom_test.f47f2b9d', defaultMessage: 'Create Custom Test' }),
        'create-smart': formatMessage({ id: 'create_custom_test.f47f2b9d', defaultMessage: 'Create Custom Test' }),
        edit: formatMessage({ id: 'edit_custom_test.878c4842', defaultMessage: 'Edit Custom Test' })
    };

    const detailsTestId = parseInt(routeTestId ?? '0', 10);

    const headerTrail: BreadcrumbLink[] = [
        { label: formatMessage({ id: 'custom_tests.9e7494ea', defaultMessage: 'Custom Tests' }), to: CUSTOM_TESTS_V2_BASE }
    ];

    if ((mode === 'edit' || mode === 'copy') && detailsTestId > 0) {

        headerTrail.push({
            label: name || (formatMessage({ id: 'test_details.88f73a4e', defaultMessage: 'Test Details' })),
            to: `${CUSTOM_TESTS_V2_BASE}/edit/${detailsTestId}`
        });

    }

    const context: CustomTestWizardContextValue = {
        name,
        bankModeId,
        categories,
        categoryId,
        editable,
        editStep,
        fetchQuestions,
        focusObjectiveId: state.focusObjectiveId,
        handleBankModeChange,
        handleLanguageChange,
        handleProductsChange,
        handleQuestionsChange,
        handleToggleCategory,
        handleToggleProduct,
        languageId,
        languages,
        lifecycleState,
        loadingProducts,
        loadingQuestions,
        minScore,
        minutes,
        mode,
        modeId,
        nameValidationError,
        orderMode,
        productIds,
        productQuestionCounts: state.productQuestionCounts,
        products,
        programDropdownKey: state.programDropdownKey,
        questionIds: state.questionIds,
        questionTree,
        randomly,
        runSmartBuild,
        sbActivityState,
        sbClassroomId,
        sbClassrooms,
        sbClassroomsError,
        sbClassroomsLoading,
        sbCustomCount,
        sbEligibleCount,
        sbMyClassroomsOnly,
        sbObjectives,
        sbPurpose,
        sbScopeType,
        sbScopeValueIds,
        sbSizeChoice,
        sbTopics,
        selectedQuestions,
        setProviderState: setState,
        setSbScopeType,
        setSbScopeValueIds,
        setSbSizeChoice,
        settingsSeen: state.settingsSeen,
        skipSmartBuild,
        smartBuildRun,
        totalQuestions: selectedQuestions,
        useRandomly,
        validationError
    };

    const handleCancelBuild = (onLeave?: () => void) => {

        const dialog = dialogRef.current;
        if (!dialog) return;

        const cancelDestination = onLeave ?? ((mode === 'edit' || mode === 'copy') && detailsTestId > 0
            ? () => navigate(`${CUSTOM_TESTS_V2_BASE}/edit/${detailsTestId}`, { state: { from: fromLookup } })
            : backToList);

        if (mode === 'edit' || mode === 'copy') {

            const isCopy = mode === 'copy';

            dialog.show({
                actions: [
                    dialog.Action({
                        label: isCopy ? (formatMessage({ id: 'discard_copy.72440b93', defaultMessage: 'Discard Copy' })) : (formatMessage({ id: 'discard_edits.082298ca', defaultMessage: 'Discard Edits' })),
                        onClick: cancelDestination,
                        variant: 'outline-danger'
                    }),
                    dialog.Action({
                        label: formatMessage({ id: 'keep_editing.92f0ec4e', defaultMessage: 'Keep Editing' }),
                        variant: 'primary'
                    })
                ],
                body: (
                    <div className={'text-center py-2'}>
                        <div
                            className={'d-flex align-items-center justify-content-center rounded-circle bg-danger text-white mx-auto mb-3'}
                            style={{ height: 48, width: 48 }}
                        >
                            <i className={'fa-solid fa-exclamation'} />
                        </div>
                        <h5 className={'fw-bold mb-2'}>
                            {isCopy
                                ? (formatMessage({ id: 'discard_this_copy.9a149c5a', defaultMessage: 'Discard this copy?' }))
                                : (formatMessage({ id: 'discard_your_edits.c4783931', defaultMessage: 'Discard your edits?' }))}
                        </h5>
                        <p className={'text-muted mb-0'}>
                            {isCopy
                                ? (formatMessage({ id: 'the_new_test_hasnt_been_saved_leaving_no.78095a6f', defaultMessage: 'The new test hasn’t been saved. Leaving now will discard your edits and the copy won’t be created.' }))
                                : (formatMessage({ id: 'your_edits_havent_been_saved_leaving_now.afe6e566', defaultMessage: 'Your edits haven’t been saved. Leaving now will discard them and the saved test won’t change.' }))}
                        </p>
                    </div>
                ),
                centered: true,
                size: 'md',
                title: isCopy ? (formatMessage({ id: 'discard_copy.72440b93', defaultMessage: 'Discard Copy' })) : (formatMessage({ id: 'discard_edits.082298ca', defaultMessage: 'Discard Edits' }))
            });

            return;

        }

        dialog.show({
            actions: [
                dialog.Action({
                    label: formatMessage({ id: 'cancel_test_build.4d0cee53', defaultMessage: 'Cancel Test Build' }),
                    onClick: cancelDestination,
                    variant: 'outline-danger'
                }),
                dialog.Action({
                    label: formatMessage({ id: 'keep_editing.92f0ec4e', defaultMessage: 'Keep Editing' }),
                    variant: 'primary'
                })
            ],
            body: (
                <div className={'text-center py-2'}>
                    <div
                        className={'d-flex align-items-center justify-content-center rounded-circle bg-danger text-white mx-auto mb-3'}
                        style={{ height: 48, width: 48 }}
                    >
                        <i className={'fa-solid fa-exclamation'} />
                    </div>
                    <h5 className={'fw-bold mb-2'}>
                        <FormattedMessage defaultMessage="Cancel Custom Test Build?" id="cancel_custom_test_build.237982fc" />
                    </h5>
                    <p className={'text-muted mb-0'}>
                        <FormattedMessage defaultMessage="Cancelling will remove all progress on this draft including your question selections, Smart Build settings, and any other test settings you’ve configured will not be saved." id="cancelling_will_remove_all_progress_on_t.9b4989a6" />
                    </p>
                </div>
            ),
            centered: true,
            size: 'md',
            title: formatMessage({ id: 'cancel_custom_test_build.237982fc', defaultMessage: 'Cancel Custom Test Build?' })
        });

    };

    // Any progress worth confirming before the user navigates away from the wizard.
    const hasUnsavedChanges = !showSuccess && (name.trim().length > 0 || selectedQuestions > 0 || stepIndex > 0);

    // Breadcrumb trail links navigate away instantly; when there is unsaved work, route
    // them through the same cancel confirmation as the Cancel button.
    const handleTrailClickCapture = (e: MouseEvent<HTMLDivElement>) => {

        if (!hasUnsavedChanges) return;

        const anchor = (e.target as HTMLElement).closest('a');
        if (!anchor) return;

        e.preventDefault();
        e.stopPropagation();

        const href = anchor.getAttribute('href') ?? CUSTOM_TESTS_V2_BASE;
        handleCancelBuild(() => navigate(href, { state: { from: fromLookup } }));

    };

    const handleAssignSaved = () => assignWizardRef.current?.show({
        modeId: state.modeId,
        onAssigned: targets => setState(prev => ({ assignmentHistory: [...prev.assignmentHistory, ...targets] })),
        testId: state.savedTestId,
        testName: name
    });
    const handleViewDetails = () => {

        if (state.savedTestId > 0) navigate(`${CUSTOM_TESTS_V2_BASE}/edit/${state.savedTestId}`, { state: { from: fromLookup } });

    };

    const handleCreateAnother = () => setState({
        name: '',
        assignmentHistory: [],
        bankModeId: 2,
        categoryId: 0,
        minScore: 70,
        minScoreTouched: false,
        minutes: 60,
        minutesTouched: false,

        // "Create another" starts a fresh create flow, so the testing mode resets to
        // unselected just like the create-mode bootstrap.
        modeId: 0,
        nameValidationError: '',
        orderMode: 'randomized',
        productIds: [],
        productQuestionCounts: [],
        products: [],
        questionIds: '',
        questionsSignature: '',
        questionsXml: '',
        questionTree: { Products: [] },
        randomly: 0,
        savedTestId: 0,
        sbActivityState: '',
        sbClassroomId: 0,
        sbCustomCount: 0,
        sbMyClassroomsOnly: true,
        sbPurpose: '',
        sbScopeType: '',
        sbScopeValueIds: [],
        sbSizeChoice: '',
        selectedQuestions: 0,
        settingsSeen: false,
        showSuccess: false,
        smartBuildRun: false,
        stepIndex: 0,
        useRandomly: false,
        validationError: ''
    });

    // Locked edit notice: the loaded test can no longer be edited (results recorded,
    // assignments in place, archived, or owned by someone else).
    const renderEditLock = () => {

        const reason = !state.isOwner
            ? (formatMessage({ id: 'only_the_owner_of_this_test_can_edit_it.d015974e', defaultMessage: 'Only the owner of this test can edit it.' }))
            : state.hasResults
                ? (formatMessage({ id: 'students_have_taken_this_test_so_its_con.1ab5f803', defaultMessage: 'Students have taken this test, so its content can no longer be edited.' }))
                : state.assignmentCount > 0
                    ? (formatMessage({ id: 'this_test_has_active_assignments_so_its.3f6f4e9a', defaultMessage: 'This test has active assignments, so its content can no longer be edited.' }))
                    : (formatMessage({ id: 'this_test_is_archived_and_cannot_be_edit.de591246', defaultMessage: 'This test is archived and cannot be edited.' }));

        return (
            <div className={'d-flex flex-column align-items-center text-center gap-3 py-5'}>

                <div
                    className={'d-flex align-items-center justify-content-center rounded-circle bg-warning text-white'}
                    style={{ height: 48, width: 48 }}
                >
                    <i className={'fa-solid fa-lock'} />
                </div>

                <div>
                    <h5 className={'fw-bold mb-1'}>
                        <FormattedMessage defaultMessage="This test is locked" id="this_test_is_locked.628a8f3e" />
                    </h5>
                    <p className={'text-muted mb-0'}>
                        {reason}
                    </p>
                </div>

                <div className={'d-flex flex-wrap justify-content-center gap-2'}>
                    <GMetrixButton
                        onClick={() => navigate(`${CUSTOM_TESTS_V2_BASE}/edit/${detailsTestId}`, { state: { from: fromLookup } })}
                        variant={'outline-primary'}
                    >
                        <FormattedMessage defaultMessage="View details" id="view_details.8327d8ee" />
                    </GMetrixButton>
                    <GMetrixButton
                        onClick={() => navigate(`${CUSTOM_TESTS_V2_BASE}/copy/${detailsTestId}`, { state: { from: fromLookup } })}
                        variant={'primary'}
                    >
                        <FormattedMessage defaultMessage="Copy test" id="copy_test.799970e9" />
                    </GMetrixButton>
                </div>

            </div>
        );

    };

    const renderStep = () => {

        switch (currentStep) {

            case EWizardStep.DETAILS:
                return <DetailsStep />;
            case EWizardStep.SMART_BUILD:
                return <SmartBuildStep />;
            case EWizardStep.QUESTIONS:
                return <QuestionsStep />;
            case EWizardStep.SETTINGS:
                return <TestSettingsStep />;
            case EWizardStep.SUMMARY:
                return <SummaryStep />;
            default:
                return null;

        }

    };

    // Locked edit: there is nothing to build, so replace the steps with the notice.
    if (!loading && mode === 'edit' && !editable) {

        return (
            <div className={'custom-test-wizard d-flex flex-column gap-3'}>

                <PageHeader
                    title={titleByMode[mode]}
                    trail={headerTrail}
                />

                <Card body>
                    {renderEditLock()}
                </Card>

            </div>
        );

    }

    return (
        <CustomTestWizardContext.Provider value={context}>
            <div className={'custom-test-wizard d-flex flex-column gap-3'}>

                <div onClickCapture={handleTrailClickCapture}>
                    <PageHeader
                        title={titleByMode[mode]}
                        trail={headerTrail}
                    />
                </div>

                {showSuccess ?
                    (
                        <Card body>
                            <WizardSuccess
                                assignmentHistory={state.assignmentHistory}
                                canAssign={savedTestId > 0}
                                canViewDetails={savedTestId > 0}
                                onAssign={handleAssignSaved}
                                onCreateAnother={handleCreateAnother}
                                onReturn={backToList}
                                onViewDetails={handleViewDetails}
                            />
                        </Card>
                    )
                    :
                    (
                        <Card body>
                            {loading ?
                                (
                                    <div className={'d-flex align-items-center justify-content-center p-5'}>
                                        <GMetrixLoader size={'large'} />
                                    </div>
                                )
                                :
                                (
                                    <div className={'d-flex flex-column gap-3'}>

                                        <GMetrixStepper
                                            linear
                                            handleChange={e => handleStepperChange(e.value)}
                                            setStep={() => undefined}
                                            step={stepIndex}
                                            stepItems={stepItems}
                                        />

                                        {currentStep === EWizardStep.SETTINGS && (
                                            <TestSummaryBar
                                                showActualTime
                                                minutes={state.minutes}
                                                questionTree={questionTree}
                                                richSummary={false}
                                                selectedQuestionIds={new Set(state.questionIds.split(',').map(s => s.trim()).filter(Boolean).map(Number))}
                                            />
                                        )}

                                        {validationError && (
                                        <Alert className={'mb-0'} variant={'danger'}>
                                            {validationError}
                                        </Alert>
                                        )}

                                        <div className={'wizard-step-body'}>
                                            {renderStep()}
                                        </div>

                                        <div
                                            className={'d-flex justify-content-between align-items-center border-top pt-3 pb-2 position-sticky bottom-0 bg-white'}
                                            style={{ zIndex: 2 }}
                                        >

                                            <div className={'d-flex align-items-center gap-3'}>
                                                <GMetrixButton
                                                    disabled={saving}
                                                    onClick={() => handleCancelBuild()}
                                                    variant={'danger'}
                                                >
                                                    <FormattedMessage defaultMessage="Cancel" id="cancel.89a86adc" />
                                                </GMetrixButton>

                                                {currentStep === EWizardStep.SMART_BUILD && (
                                                    <button className={'btn btn-link text-decoration-none fw-semibold p-0'} onClick={skipSmartBuild} type={'button'}>
                                                        <span className={'d-inline-flex align-items-center gap-2'}>
                                                            <FormattedMessage defaultMessage="Skip and choose questions manually" id="skip_and_choose_questions_manually.4df132d6" />
                                                            <i className={'fa-solid fa-arrow-right'} />
                                                        </span>
                                                    </button>
                                                )}
                                            </div>

                                            <div className={'d-flex align-items-center gap-2'}>

                                                {stepIndex > 0 && (
                                                    <GMetrixButton
                                                        disabled={saving}
                                                        onClick={handleBack}
                                                        variant={'outline-secondary'}
                                                    >
                                                        <span className={'d-inline-flex align-items-center gap-2'}>
                                                            <i className={'fa-solid fa-arrow-left'} />
                                                            {`${formatMessage({ id: 'previous.f825144b', defaultMessage: 'Previous' })}: ${stepLabels[steps[stepIndex - 1]]}`}
                                                        </span>
                                                    </GMetrixButton>
                                                )}

                                                {currentStep === EWizardStep.SMART_BUILD && (
                                                    <GMetrixButton
                                                        disabled={!sbCanGenerate}
                                                        onClick={runSmartBuild}
                                                        variant={'primary'}
                                                    >
                                                        <span className={'d-inline-flex align-items-center gap-2'}>
                                                            {sbGenerating
                                                                ? (formatMessage({ id: 'generating.aea86213', defaultMessage: 'Generating…' }))
                                                                : (formatMessage({ id: 'generate_test_draft.c5d67582', defaultMessage: 'Generate Test Draft' }))}
                                                            <i className={'fa-regular fa-lightbulb'} />
                                                        </span>
                                                    </GMetrixButton>
                                                )}

                                                {currentStep !== EWizardStep.SMART_BUILD && (isLastStep ?
                                                    (
                                                        <GMetrixButton
                                                            disabled={saving}
                                                            onClick={handleSave}
                                                            variant={'primary'}
                                                        >
                                                            {saving
                                                                ? (formatMessage({ id: 'saving.c916139e', defaultMessage: 'Saving...' }))
                                                                : (formatMessage({ id: 'save_custom_test.d45c639f', defaultMessage: 'Save Custom Test' }))}
                                                        </GMetrixButton>
                                                    )
                                                    :
                                                    (
                                                        <GMetrixButton
                                                            disabled={validating || saving}
                                                            onClick={handleNext}
                                                            variant={'primary'}
                                                        >
                                                            {validating ?
                                                                (formatMessage({ id: 'validating.e1043608', defaultMessage: 'Validating...' }))
                                                                :
                                                                (
                                                                    <span className={'d-inline-flex align-items-center gap-2'}>
                                                                        {`${formatMessage({ id: 'next.50b7834e', defaultMessage: 'Next' })}: ${stepLabels[steps[stepIndex + 1]]}`}
                                                                        <i className={'fa-solid fa-arrow-right'} />
                                                                    </span>
                                                                )}
                                                        </GMetrixButton>
                                                    ))}

                                            </div>

                                        </div>

                                    </div>
                                )}
                        </Card>
                    )}

                <AssignWizard ref={assignWizardRef} />

                <Dialog ref={dialogRef} />

            </div>
        </CustomTestWizardContext.Provider>
    );

};
